// https://www.kunkalabs.com/mixitup/docs/get-started/
import mixitup from 'mixitup';

const mixitupAnimation = {
	animateResizeTargets: true,
	duration: 550,
	nudge: true,
	reverseOut: false,
	effects: 'fade scale(0.01) translateZ(-100px)',
};

const PROJEKTE_RETURN_PARAM_KEYS = [ 'from', 'kat', 'status', 'filters' ];

function selectorToIds( selectors, prefix ) {
	const regex = new RegExp( `^\\.${ prefix }-(\\d+)$` );

	return selectors
		.map( ( selector ) => {
			const match = selector.match( regex );
			return match ? match[ 1 ] : null;
		} )
		.filter( Boolean );
}

function hasProjekteReturnState( activeFilters, filtersOpen ) {
	return (
		filtersOpen ||
		activeFilters.kategorien.length > 0 ||
		activeFilters.status.length > 0
	);
}

function buildMixitupFilterSelector( activeFilters ) {
	const { kategorien, status } = activeFilters;
	const selectors              = [ ...kategorien, ...status ];

	if ( ! selectors.length ) {
		return 'all';
	}

	// AND across all active filters (element must match every selected class).
	return selectors.join( '' );
}

function isMixitupContainerVisible( container ) {
	return container.getClientRects().length > 0;
}

function buildProjekteReturnQuery( activeFilters, filtersOpen, forSingle = true ) {
	if ( ! hasProjekteReturnState( activeFilters, filtersOpen ) ) {
		return null;
	}

	const params = new URLSearchParams();

	if ( forSingle ) {
		params.set( 'from', 'projekte' );
	}

	const katIds     = selectorToIds( activeFilters.kategorien, 'kat' );
	const statusIds  = selectorToIds( activeFilters.status, 'status' );

	if ( katIds.length ) {
		params.set( 'kat', katIds.join( ',' ) );
	}
	if ( statusIds.length ) {
		params.set( 'status', statusIds.join( ',' ) );
	}
	if ( filtersOpen ) {
		params.set( 'filters', 'open' );
	}

	return params;
}

function stripProjekteReturnParams( url ) {
	const parsed = new URL( url, window.location.origin );

	PROJEKTE_RETURN_PARAM_KEYS.forEach( ( key ) => {
		parsed.searchParams.delete( key );
	} );

	return parsed;
}

function initProjekteMixitup() {
	const containers = document.querySelectorAll( '.projekte__mixit' );
	if ( ! containers.length ) {
		return null;
	}

	const mixers = [];
	containers.forEach( ( container ) => {
		const isAccordionList = container.classList.contains( 'accordion--simple' );

		mixers.push( {
			container,
			instance: mixitup( container, {
				animation: {
					...mixitupAnimation,
					animateResizeTargets: ! isAccordionList,
					nudge: ! isAccordionList,
				},
				controls: {
					enable: false,
				},
				load: {
					filter: 'all',
				},
			} ),
		} );
	} );

	const activeFilters = {
		kategorien: [],
		status: [],
	};

	const $projekteFilters = $( '.projekte-filters' );

	function hasActiveFilters() {
		return (
			activeFilters.kategorien.length > 0 ||
			activeFilters.status.length > 0
		);
	}

	function isFiltersPanelOpen() {
		return $projekteFilters.length && $projekteFilters.hasClass( 'is-open' );
	}

	function applyFilter( animate = true ) {
		const selector = buildMixitupFilterSelector( activeFilters );

		return Promise.all(
			mixers.map( ( { container, instance } ) => {
				const shouldAnimate =
					animate && isMixitupContainerVisible( container );

				return instance.filter( selector, shouldAnimate );
			} )
		).then( () => {
			mixers.forEach( ( { container, instance } ) => {
				if ( isMixitupContainerVisible( container ) ) {
					instance.forceRefresh();
				}
			} );
		} );
	}

	function syncOverviewUrl( filtersOpen ) {
		const overviewParams = buildProjekteReturnQuery(
			activeFilters,
			filtersOpen,
			false
		);
		const overviewUrl    = new URL( window.location.href );

		PROJEKTE_RETURN_PARAM_KEYS.forEach( ( key ) => {
			overviewUrl.searchParams.delete( key );
		} );

		if ( overviewParams ) {
			overviewParams.forEach( ( value, key ) => {
				overviewUrl.searchParams.set( key, value );
			} );
		}

		window.history.replaceState( null, '', overviewUrl.toString() );
	}

	function updateProjectLinkHrefs() {
		const filtersOpen  = isFiltersPanelOpen();
		const singleParams = buildProjekteReturnQuery(
			activeFilters,
			filtersOpen,
			true
		);

		document.querySelectorAll( '.projekte__mixit a[href]' ).forEach( ( anchor ) => {
			const clean = stripProjekteReturnParams( anchor.href );

			if ( singleParams ) {
				singleParams.forEach( ( value, key ) => {
					clean.searchParams.set( key, value );
				} );
			}

			anchor.href = clean.toString();
		} );

		syncOverviewUrl( filtersOpen );
	}

	function resetFilters( animate = true ) {
		activeFilters.kategorien = [];
		activeFilters.status     = [];

		$projekteFilters
			.find( '.mixitup-control' )
			.removeClass( 'mixitup-control-active' )
			.attr( 'aria-pressed', 'false' );

		return applyFilter( animate ).then( () => {
			updateProjectLinkHrefs();
		} );
	}

	function restoreFiltersFromUrl() {
		const params      = new URLSearchParams( window.location.search );
		const katRaw      = params.get( 'kat' );
		const statusRaw   = params.get( 'status' );
		const filtersOpen = params.get( 'filters' ) === 'open';

		const katIds = katRaw
			? katRaw.split( ',' ).map( ( id ) => id.trim() ).filter( Boolean )
			: [];
		const statusIds = statusRaw
			? statusRaw.split( ',' ).map( ( id ) => id.trim() ).filter( Boolean )
			: [];

		katIds.forEach( ( id ) => {
			const selector = `.kat-${ id }`;
			const $control = $(
				`.projekte-filters .mixitup-control[data-filter="${ selector }"]`
			);

			if ( ! $control.length ) {
				return;
			}

			$control.addClass( 'mixitup-control-active' );
			$control.attr( 'aria-pressed', 'true' );
			activeFilters.kategorien.push( selector );
		} );

		statusIds.forEach( ( id ) => {
			const selector = `.status-${ id }`;
			const $control = $(
				`.projekte-filters .mixitup-control[data-filter="${ selector }"]`
			);

			if ( ! $control.length ) {
				return;
			}

			$control.addClass( 'mixitup-control-active' );
			$control.attr( 'aria-pressed', 'true' );
			activeFilters.status.push( selector );
		} );

		if ( ( filtersOpen || hasActiveFilters() ) && $projekteFilters.length ) {
			$projekteFilters.addClass( 'is-open' );
			$projekteFilters
				.find( '.projekte-filter__toggle' )
				.attr( 'aria-expanded', 'true' );
		}

		return hasActiveFilters();
	}

	function runInitialRestore() {
		const needsFilter = restoreFiltersFromUrl();

		if ( ! needsFilter ) {
			updateProjectLinkHrefs();
			return;
		}

		applyFilter( false ).then( () => {
			updateProjectLinkHrefs();
		} );
	}

	$( '.projekte-filters' ).on( 'click', '.mixitup-control', function () {
		const $control = $( this );
		const $group   = $control.closest( '[data-filter-group]' );
		const group    = $group.attr( 'data-filter-group' );
		const filter   = $control.attr( 'data-filter' );

		if ( ! group || ! filter ) {
			return;
		}

		const isActive = $control.hasClass( 'mixitup-control-active' );

		if ( isActive ) {
			$control.removeClass( 'mixitup-control-active' );
			$control.attr( 'aria-pressed', 'false' );
			activeFilters[ group ] = activeFilters[ group ].filter(
				( item ) => item !== filter
			);
		} else {
			$control.addClass( 'mixitup-control-active' );
			$control.attr( 'aria-pressed', 'true' );
			activeFilters[ group ].push( filter );
		}

		applyFilter().then( () => {
			updateProjectLinkHrefs();
		} );
	} );

	if ( $projekteFilters.length ) {
		$projekteFilters.on( 'click', '.projekte-filter__toggle', function () {
			const $toggle = $( this );
			const $root   = $toggle.closest( '.projekte-filters' );
			const wasOpen = $root.hasClass( 'is-open' );
			const isOpen  = $root.toggleClass( 'is-open' ).hasClass( 'is-open' );

			$toggle.attr( 'aria-expanded', isOpen ? 'true' : 'false' );

			if ( wasOpen && ! isOpen && hasActiveFilters() ) {
				resetFilters();
				return;
			}

			updateProjectLinkHrefs();
		} );
	}

	requestAnimationFrame( () => {
		requestAnimationFrame( runInitialRestore );
	} );

	$( document ).on( 'home-start:viewchange', '.home-start', function () {
		const $root = $( this );

		if ( ! $root.find( '.projekte__mixit' ).length ) {
			return;
		}

		requestAnimationFrame( () => {
			mixers.forEach( ( { container, instance } ) => {
				if ( isMixitupContainerVisible( container ) ) {
					instance.forceRefresh();
				}
			} );
		} );
	} );

	return mixers;
}

$( document ).ready( function () {
	if ( $( '.projekte__mixit' ).length ) {
		initProjekteMixitup();
		return;
	}

	let initialFilter = 'all';
	const hash        = window.location.hash.replace( /^#/g, '' );
	if ( hash ) {
		initialFilter = '.' + hash;
	}

	if ( $( '.mixit--filter' ).length ) {
		mixitup( '.mixit--filter', {
			animation: mixitupAnimation,
			selectors: {
				control: '.mixitup-control',
			},
			load: {
				filter: initialFilter,
			},
		} );
	}
} );
